// features/admin/generatedReportsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface GeneratedReport {
  id: number;
  format_id: number;
  format_name: string;
  report_type: string;
  from_date: string;
  to_date: string;
  export_type: string;
  generated_by: string;
  status: 'generating' | 'ready' | 'failed';
  total_records: number | null;
  file_path_csv: string | null;
  file_path_pdf: string | null;
  download_url_csv: string | null;
  download_url_pdf: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ReportApiResponse<T = any> {
  error: boolean;
  message?: string;
  data?: T;
}

// ─── Request Types ────────────────────────────────────────────────────────────

export interface ListReportsRequest {
  report_type?: string;
  status?: string;
}

export interface GenerateReportRequest {
  format_id: number;
  from_date: string; // 'YYYY-MM-DD'
  to_date: string;   // 'YYYY-MM-DD'
}

export interface DeleteReportRequest {
  id: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const REPORT_TYPE_FILTER_OPTIONS = [
  { label: 'All Types',        value: '' },
  { label: 'Sales Report',     value: 'Sales Report' },
  { label: 'Order Report',     value: 'Order Report' },
  { label: 'Product Report',   value: 'Product Report' },
  { label: 'User Report',      value: 'User Report' },
  { label: 'Customer Report',  value: 'Customer Report' },
  { label: 'Inventory Report', value: 'Inventory Report' },
  { label: 'Stock Report',     value: 'Stock Report' },
  { label: 'Return Report',    value: 'Return Report' },
  { label: 'Refund Report',    value: 'Refund Report' },
  { label: 'Wallet Report',    value: 'Wallet Report' },
  { label: 'Credit Report',    value: 'Credit Report' },
  { label: 'Payment Report',   value: 'Payment Report' },
  { label: 'Delivery Report',  value: 'Delivery Report' },
  { label: 'Referral Report',  value: 'Referral Report' },
  { label: 'Reward Report',    value: 'Reward Report' },
  { label: 'Coupon Report',    value: 'Coupon Report' },
];

export const REPORT_STATUS_OPTIONS = [
  { label: 'All Status',  value: '' },
  { label: 'Ready',       value: 'ready' },
  { label: 'Generating',  value: 'generating' },
  { label: 'Failed',      value: 'failed' },
];

// ─── API Config ───────────────────────────────────────────────────────────────

const GENERATED_REPORTS_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT,
  ENDPOINTS: {
    LIST:     '/admin/list-reports',
    GENERATE: '/admin/generate-report',
    DELETE:   '/admin/delete-report',
  },
};

// ─── API Slice ────────────────────────────────────────────────────────────────

export const generatedReportsApi = createApi({
  reducerPath: 'generatedReportsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: GENERATED_REPORTS_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['GeneratedReports'],
  endpoints: (build) => ({

    // ── List Reports ──────────────────────────────────────────────────────────
    listReports: build.query<ReportApiResponse<GeneratedReport[]>, ListReportsRequest>({
      query: (body = {}) => ({
        url: GENERATED_REPORTS_CONFIG.ENDPOINTS.LIST,
        method: 'POST',
        body,
      }),
      providesTags: ['GeneratedReports'],
      transformResponse: (response: any): ReportApiResponse<GeneratedReport[]> => ({
        error:   response?.error ?? false,
        message: response?.message || '',
        data:    response?.data   || [],
      }),
      transformErrorResponse: (response: any) => ({
        error:   true,
        message: response.data?.message || 'Failed to load reports',
        data:    [],
      }),
    }),

    // ── Generate Report ───────────────────────────────────────────────────────
    generateReport: build.mutation<ReportApiResponse<GeneratedReport>, GenerateReportRequest>({
      query: (body) => ({
        url: GENERATED_REPORTS_CONFIG.ENDPOINTS.GENERATE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GeneratedReports'],
      transformResponse: (response: any): ReportApiResponse<GeneratedReport> => ({
        error:   response?.error ?? false,
        message: response?.message || 'Report generated successfully',
        data:    response?.data,
      }),
      transformErrorResponse: (response: any) => ({
        error:   true,
        message: response.data?.message || 'Failed to generate report',
      }),
    }),

    // ── Delete Report ─────────────────────────────────────────────────────────
    deleteReport: build.mutation<ReportApiResponse, DeleteReportRequest>({
      query: (body) => ({
        url: GENERATED_REPORTS_CONFIG.ENDPOINTS.DELETE,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GeneratedReports'],
      transformResponse: (response: any): ReportApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Report deleted successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error:   true,
        message: response.data?.message || 'Failed to delete report',
      }),
    }),

  }),
});

export const {
  useListReportsQuery,
  useGenerateReportMutation,
  useDeleteReportMutation,
} = generatedReportsApi;